import React, { useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LabelList,
} from "recharts";
import { Form } from "react-bootstrap";
import CustomLegend from "../CustomLegend";
import CustomTooltip from "../CustomTooltip";
const StaticStatusChart = ({ data }) => {
  const [showTable, setShowTable] = useState(false);
  const [selectedYear, setSelectedYear] = useState(2023);

  // Sorting state
  const [sortConfig, setSortConfig] = useState({
    key: "place", // default sort by place
    direction: "asc",
  });
  // helper function for color coding
  const getColor = (value) => {
    if (value < 80) return "#dc3545"; // Red
    if (value < 90) return "#fd7e14"; // Orange
    if (value < 95) return "#ffc107"; // Yellow
    return "#28a745"; // Green
  };

  const getTextColor = (value) => {
    if (value < 80) return "white"; // Red
    if (value < 90) return "white"; // Orange
    if (value < 95) return "black"; // Yellow
    return "white"; // Green
  };

  if (!Array.isArray(data) || data.length === 0) {
    return (
      <div
        style={{
          textAlign: "center",
          padding: "40px",
          backgroundColor: "#fff",
          border: "1px solid #ddd",
          borderRadius: "8px",
          marginTop: "20px",
        }}
      >
        <h5 style={{ color: "#666", fontSize: "20px" }}>No data available</h5>
      </div>
    );
  }

  const filteredData = data.filter(
    (item) => Number(item.year) === Number(selectedYear),
  );

  const statusData = filteredData.map((item) => ({
    place: item.Place,
    First95: Number(item.Status1),
    Second95: Number(item.Status2),
    Third95: Number(item.Status3),
  }));

  // Sorting logic
  const sortedData = [...statusData].sort((a, b) => {
    if (a[sortConfig.key] < b[sortConfig.key]) {
      return sortConfig.direction === "asc" ? -1 : 1;
    }
    if (a[sortConfig.key] > b[sortConfig.key]) {
      return sortConfig.direction === "asc" ? 1 : -1;
    }
    return 0;
  });

  // Handle sorting click
  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return {
          key,
          direction: prev.direction === "asc" ? "desc" : "asc",
        };
      } else {
        return { key, direction: "asc" };
      }
    });
  };

  // Icon display function
  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return "⬍"; // default icon
    return sortConfig.direction === "asc" ? "▲" : "▼";
  };

  const chunkArray = (array, chunkCount) => {
    const result = [];
    const chunkSize = Math.ceil(array.length / chunkCount);

    for (let i = 0; i < array.length; i += chunkSize) {
      result.push(array.slice(i, i + chunkSize));
    }

    return result;
  };

  const getChartCount = (length) => {
   if (length < 9) return 1;
    if (length >= 9 && length <= 18) return 2;
    if (length <= 45) return 3;
    return Math.ceil(length / 20);
  };

  // 👉 prepare chart data
  const chartCount = getChartCount(statusData.length);
  const chunkedData = chunkArray(statusData, chartCount);

  return (
    <div style={{ width: "100%", position: "relative" }}>
      {/* Toggle Switch */}
      <div className="card hptCard mt-4 border-0">
        <div class="card-header brBtomlr">
          <div class="headnumText justify-content-between">
            <div class="cardTitle d-flex align-items-center gap-3">
              <h5>95-95-99 Status of State & UT</h5>
              <Form.Select
                size="sm"
                style={{ width: "120px" }}
                value={selectedYear}
                onChange={(e) => setSelectedYear(Number(e.target.value))}
              >
                <option value={2023}>2023</option>
                <option value={2025}>2025</option>
              </Form.Select>
            </div>

            <div class="cardDropDown">
              <span style={{ marginRight: "10px", fontWeight: 500 }}>
                {showTable ? "Chart View" : "Table View"}
              </span>
              <Form>
                <Form.Check
                  type="switch"
                  id="chart-table-toggle"
                  onChange={() => setShowTable((prev) => !prev)}
                  checked={showTable}
                />
              </Form>
            </div>
          </div>
        </div>
      </div>

      {showTable ? (
        // Table View
        <div className="card hptCard mt-4 border-0">
          <div
            style={{
              display: "flex",
              gap: "20px",
              padding: "10px 15px",
              borderBottom: "1px solid #ddd",
              fontSize: "14px",
              fontWeight: 500,
              justifyContent: "flex-end",
            }}
          >
            <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <span
                style={{
                  width: "14px",
                  height: "14px",
                  borderRadius: "2px",
                  backgroundColor: "#dc3545",
                }}
              ></span>
              {"< 80%"}
            </span>

            <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <span
                style={{
                  width: "14px",
                  height: "14px",
                  borderRadius: "2px",
                  backgroundColor: "#fd7e14",
                }}
              ></span>
              {"80–89%"}
            </span>

            <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <span
                style={{
                  width: "14px",
                  height: "14px",
                  borderRadius: "2px",
                  backgroundColor: "#ffc107",
                }}
              ></span>
              {"90–94%"}
            </span>

            <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <span
                style={{
                  width: "14px",
                  height: "14px",
                  borderRadius: "2px",
                  backgroundColor: "#28a745",
                }}
              ></span>
              {"95%+"}
            </span>
          </div>
          <div className="table-responsive">
            <table className="table table-bordered ">
              <thead className="thead-light">
                <tr>
                  <th className="fw-bold">States</th>
                  <th
                    className="text-center fw-bold cursor-pointer"
                    onClick={() => handleSort("First95")}
                  >
                    First 95 {getSortIcon("First95")}
                  </th>
                  <th
                    className="text-center fw-bold cursor-pointer"
                    onClick={() => handleSort("Second95")}
                  >
                    Second 95 {getSortIcon("Second95")}
                  </th>
                  <th
                    className="text-center fw-bold cursor-pointer"
                    onClick={() => handleSort("Third95")}
                  >
                    Third 99 {getSortIcon("Third95")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedData.map((item, idx) => (
                  <tr key={idx}>
                    <td className="fw-bold">{item.place}</td>
                    <td
                      style={{
                        backgroundColor: getColor(item.First95),
                        textAlign: "center",
                        color: getTextColor(item.First95),
                        fontSize: "18px",
                        // color: "white",
                        fontWeight: "bold",
                      }}
                    >
                      {item.First95}%
                    </td>
                    <td
                      style={{
                        backgroundColor: getColor(item.Second95),
                        textAlign: "center",
                        // color: "white",
                        color: getTextColor(item.Second95),
                        fontSize: "18px",
                        fontWeight: "bold",
                      }}
                    >
                      {item.Second95}%
                    </td>
                    <td
                      style={{
                        backgroundColor: getColor(item.Third95),
                        textAlign: "center",
                        color: getTextColor(item.Third95),
                        fontSize: "18px",
                        // color: "white",
                        fontWeight: "bold",
                      }}
                    >
                      {item.Third95}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        // Chart View
        <div>
          {/* <div className="card hptCard mt-4 border-0">
            <ResponsiveContainer width="100%" height={650}>
              <BarChart
                width={500}
                height={600}
                data={statusData.slice(0, 18)}
                margin={{
                  top: 20,
                  right: 30,
                  left: 20,
                  bottom: 250,
                }}
              >
                <XAxis
                  dataKey="place"
                  angle={-90}
                  textAnchor="end"
                  interval={0}
                />
                <YAxis />

                <Tooltip content={<CustomTooltip />} />
                <Legend
                  verticalAlign="top"
                  content={
                    <CustomLegend
                      order={["First 95", "Second 95", "Third 99"]}
                    />
                  }
                />
                <Bar dataKey="First95" fill="#00a084" name="First 95">
                  <LabelList
                    dataKey="First95"
                    position="top"
                    content={({ x, y, width, value }) => {
                      if (value == null) return null;
                      return (
                        <text
                          x={x + width / 2} // center horizontally
                          y={y - 5} // place above bar
                          fontSize={12}
                          fontWeight="bold"
                          textAnchor="start" // 👈 try "start", "middle", or "end"
                          transform={`rotate(-90, ${x + width / 2}, ${y - 5})`}
                        >
                          {value}
                        </text>
                      );
                    }}
                  />
                </Bar>
                <Bar dataKey="Second95" fill="#ffc40c" name="Second 95">
                  <LabelList
                    dataKey="Second95"
                    position="top"
                    content={({ x, y, width, value }) => {
                      if (value == null) return null;
                      return (
                        <text
                          x={x + width / 2} // center horizontally
                          y={y - 5} // place above bar
                          fontSize={12}
                          fontWeight="bold"
                          textAnchor="start" // 👈 try "start", "middle", or "end"
                          transform={`rotate(-90, ${x + width / 2}, ${y - 5})`}
                        >
                          {value}
                        </text>
                      );
                    }}
                  />
                </Bar>
                <Bar dataKey="Third95" fill="#dd1e47" name="Third 99">
                  <LabelList
                    dataKey="Third95"
                    position="top"
                    content={({ x, y, width, value }) => {
                      if (value == null) return null;
                      return (
                        <text
                          x={x + width / 2} // center horizontally
                          y={y - 5} // place above bar
                          fontSize={12}
                          fontWeight="bold"
                          textAnchor="start" // 👈 try "start", "middle", or "end"
                          transform={`rotate(-90, ${x + width / 2}, ${y - 5})`}
                        >
                          {value}
                        </text>
                      );
                    }}
                  />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div> */}
          {chunkedData.map((chunk, index) => (
            <div key={index} className="card hptCard mt-4 border-0">
              <ResponsiveContainer width="100%" height={650}>
                <BarChart
                  width={500}
                  height={600}
                  data={chunk} // remaining
                  margin={{
                    top: 20,
                    right: 30,
                    left: 20,
                    bottom: 250,
                  }}
                >
                  <XAxis
                    dataKey="place"
                    angle={-90}
                    textAnchor="end"
                    interval={0}
                  />
                  <YAxis />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend
                    verticalAlign="top"
                    content={
                      <CustomLegend
                        order={["First 95", "Second 95", "Third 99"]}
                      />
                    }
                  />

                  <Bar dataKey="First95" fill="#00a084" name="First 95">
                    <LabelList
                      dataKey="First95"
                      position="top"
                      content={({ x, y, width, value }) => {
                        if (value == null) return null;
                        return (
                          <text
                            x={x + width / 2} // center horizontally
                            y={y - 5} // place above bar
                            fontSize={12}
                            fontWeight="bold"
                            textAnchor="start" // 👈 try "start", "middle", or "end"
                            transform={`rotate(-90, ${x + width / 2}, ${
                              y - 5
                            })`}
                          >
                            {value}
                          </text>
                        );
                      }}
                    />
                  </Bar>
                  <Bar dataKey="Second95" fill="#ffc40c" name="Second 95">
                    <LabelList
                      dataKey="Second95"
                      position="top"
                      content={({ x, y, width, value }) => {
                        if (value == null) return null;
                        return (
                          <text
                            x={x + width / 2} // center horizontally
                            y={y - 5} // place above bar
                            fontSize={12}
                            fontWeight="bold"
                            textAnchor="start" // 👈 try "start", "middle", or "end"
                            transform={`rotate(-90, ${x + width / 2}, ${
                              y - 5
                            })`}
                          >
                            {value}
                          </text>
                        );
                      }}
                    />
                  </Bar>
                  <Bar dataKey="Third95" fill="#dd1e47" name="Third 99">
                    <LabelList
                      dataKey="Third95"
                      position="top"
                      content={({ x, y, width, value }) => {
                        if (value == null) return null;
                        return (
                          <text
                            x={x + width / 2} // center horizontally
                            y={y - 5} // place above bar
                            fontSize={12}
                            fontWeight="bold"
                            textAnchor="start" // 👈 try "start", "middle", or "end"
                            transform={`rotate(-90, ${x + width / 2}, ${
                              y - 5
                            })`}
                          >
                            {value}
                          </text>
                        );
                      }}
                    />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default StaticStatusChart;
